/**
 * MilestoneChecklist Component
 * Displays milestones with checkboxes, supporting multiple babies
 */

import { useState } from 'react';
import { Check, MessageSquare, Milestone } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { MilestoneWithTracking, BabyInfo } from '../types';
import { MilestoneCommentModal } from './MilestoneCommentModal';

interface MilestoneChecklistProps {
  milestones: MilestoneWithTracking[];
  babies: BabyInfo[];
  onCheck: (params: { babyIndex: number; milestoneId: string; notes?: string }) => Promise<void>;
  onUncheck: (params: { babyIndex: number; milestoneId: string }) => Promise<void>;
  onUpdateNotes: (params: { babyIndex: number; milestoneId: string; notes: string }) => Promise<void>;
  isLoading?: boolean;
  isUpdating?: boolean;
}

interface CommentModalState {
  isOpen: boolean;
  milestone: MilestoneWithTracking | null;
  babyIndex: number;
  existingNotes: string;
  isChecked: boolean;
}

export function MilestoneChecklist({
  milestones,
  babies,
  onCheck,
  onUncheck,
  onUpdateNotes,
  isLoading,
  isUpdating,
}: MilestoneChecklistProps) {
  const { t, i18n } = useTranslation('babyDevelopment');
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const [commentModal, setCommentModal] = useState<CommentModalState>({
    isOpen: false,
    milestone: null,
    babyIndex: 0,
    existingNotes: '',
    isChecked: false,
  });

  const isMultipleBabies = babies.length > 1;

  const handleToggle = async (milestone: MilestoneWithTracking, babyIndex: number) => {
    const tracking = milestone.baby_tracking[babyIndex];
    const isChecked = !!tracking;

    if (isChecked) {
      await onUncheck({ babyIndex, milestoneId: milestone.id });
    } else {
      await onCheck({ babyIndex, milestoneId: milestone.id });
    }
  };

  const openCommentModal = (milestone: MilestoneWithTracking, babyIndex: number) => {
    const tracking = milestone.baby_tracking[babyIndex];
    setCommentModal({
      isOpen: true,
      milestone,
      babyIndex,
      existingNotes: tracking?.notes || '',
      isChecked: !!tracking,
    });
  };

  const handleSaveComment = async (notes: string) => {
    if (!commentModal.milestone) return;

    if (commentModal.isChecked) {
      // Update existing milestone notes
      await onUpdateNotes({
        babyIndex: commentModal.babyIndex,
        milestoneId: commentModal.milestone.id,
        notes,
      });
    } else {
      // Check milestone with notes
      await onCheck({
        babyIndex: commentModal.babyIndex,
        milestoneId: commentModal.milestone.id,
        notes,
      });
    }

    setCommentModal({ ...commentModal, isOpen: false });
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Milestone className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('milestones.title')}</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-white/10 rounded-[15px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (milestones.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="flex items-center gap-2 mb-4">
          <Milestone className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('milestones.title')}</h3>
        </div>
        <p className="text-white/60">{t('milestones.none')}</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Milestone className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('milestones.title')}</h3>
        </div>

        <div className="space-y-3">
          {milestones.map((milestone) => {
            // For single baby mode, get the tracking state
            const singleBabyTracking = !isMultipleBabies ? milestone.baby_tracking[0] : null;
            const singleBabyChecked = !isMultipleBabies && !!singleBabyTracking;

            return (
              <div
                key={milestone.id}
                className="glass-effect rounded-[15px] p-4"
              >
                {/* Single Baby: Checkbox + Title inline */}
                {!isMultipleBabies ? (
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => handleToggle(milestone, 0)}
                      disabled={isUpdating}
                      className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${
                        singleBabyChecked
                          ? 'bg-pp-teal text-white'
                          : 'bg-white/10 border border-white/20 hover:border-white/40'
                      } ${isUpdating ? 'opacity-50' : ''}`}
                    >
                      {singleBabyChecked && <Check className="w-4 h-4" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className={`font-medium ${singleBabyChecked ? 'text-white' : 'text-white/90'}`}>
                          {milestone.title}
                        </h4>
                        {/* Comment Button */}
                        <button
                          onClick={() => openCommentModal(milestone, 0)}
                          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                            singleBabyTracking?.notes
                              ? 'text-pp-teal hover:bg-pp-teal/20'
                              : 'text-white/40 hover:text-white/60 hover:bg-white/10'
                          }`}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </button>
                      </div>

                      {milestone.description && (
                        <p className="text-white/50 text-sm mt-1">{milestone.description}</p>
                      )}

                      {/* Tracking Info */}
                      {singleBabyChecked && singleBabyTracking && (
                        <span className="text-white/40 text-xs block mt-2">
                          {new Date(singleBabyTracking.achieved_at).toLocaleDateString(dateLocale, {
                            day: '2-digit',
                            month: '2-digit',
                          })}
                          {singleBabyTracking.checked_by_profile?.display_name && (
                            <> {t('milestones.by', { name: singleBabyTracking.checked_by_profile.display_name })}</>
                          )}
                        </span>
                      )}

                      {/* Notes */}
                      {singleBabyTracking?.notes && (
                        <div className="mt-2 pl-3 border-l-2 border-pp-teal/30">
                          <p className="text-white/60 text-sm italic">
                            "{singleBabyTracking.notes}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Multiple Babies: Title first, then checkboxes */
                  <>
                    <div className="mb-3">
                      <h4 className="text-white font-medium">{milestone.title}</h4>
                      {milestone.description && (
                        <p className="text-white/50 text-sm mt-1">{milestone.description}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      {babies.map((baby) => {
                        const tracking = milestone.baby_tracking[baby.index];
                        const isChecked = !!tracking;

                        return (
                          <div key={baby.index} className="flex items-center gap-3 pl-2">
                            {/* Checkbox */}
                            <button
                              onClick={() => handleToggle(milestone, baby.index)}
                              disabled={isUpdating}
                              className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                isChecked
                                  ? 'bg-pp-teal text-white'
                                  : 'bg-white/10 border border-white/20 hover:border-white/40'
                              } ${isUpdating ? 'opacity-50' : ''}`}
                            >
                              {isChecked && <Check className="w-4 h-4" />}
                            </button>

                            {/* Baby Name */}
                            <span className={`text-sm ${isChecked ? 'text-white' : 'text-white/60'}`}>
                              {baby.name}
                            </span>

                            {/* Tracking Info */}
                            {isChecked && tracking && (
                              <span className="text-white/40 text-xs flex-1">
                                {new Date(tracking.achieved_at).toLocaleDateString(dateLocale, {
                                  day: '2-digit',
                                  month: '2-digit',
                                })}
                                {tracking.checked_by_profile?.display_name && (
                                  <> {t('milestones.by', { name: tracking.checked_by_profile.display_name })}</>
                                )}
                              </span>
                            )}

                            {/* Comment Button */}
                            <button
                              onClick={() => openCommentModal(milestone, baby.index)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                tracking?.notes
                                  ? 'text-pp-teal hover:bg-pp-teal/20'
                                  : 'text-white/40 hover:text-white/60 hover:bg-white/10'
                              }`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Modal */}
      <MilestoneCommentModal
        isOpen={commentModal.isOpen}
        onClose={() => setCommentModal({ ...commentModal, isOpen: false })}
        onSave={handleSaveComment}
        milestoneTitle={commentModal.milestone?.title || ''}
        babyName={isMultipleBabies ? babies.find(b => b.index === commentModal.babyIndex)?.name : undefined}
        existingNotes={commentModal.existingNotes}
        isChecked={commentModal.isChecked}
      />
    </>
  );
}
